import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Check, Dumbbell, Zap, Trophy, LogOut, Shield } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { API } from '../App';

const Onboarding = ({ onComplete, onLogout }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [legalAcknowledged, setLegalAcknowledged] = useState(false);
  
  const [formData, setFormData] = useState({
    skill_level: '',
    fitness_goal: 'maintain',
    home_equipment: [],
    age: '',
    height_feet: '',
    height_inches: '',
    starting_weight: '',
    goal_weight: '',
    weight_unit: 'lbs',
    weight_calibration: '',
    injuries: '',
    weak_point: 'none'
  });

  const equipmentOptions = [
    { id: 'none', label: 'No Equipment (Bodyweight Only)', icon: '💪' },
    { id: 'dumbbells', label: 'Dumbbells', icon: '🔩' },
    { id: 'barbell', label: 'Barbell', icon: '🏋️‍♂️' },
    { id: 'bench', label: 'Bench (Flat/Adjustable)', icon: '🛏️' },
    { id: 'pullup_bar', label: 'Pull-up Bar', icon: '🦾' },
    { id: 'dip_bar', label: 'Dip / Parallel Bars', icon: '\u{1F4AA}' },
    { id: 'rings', label: 'Gymnastic Rings', icon: '\u25EF\u25EF' },
    { id: 'resistance_bands', label: 'Resistance Bands', icon: '〰️' },
    { id: 'kettlebells', label: 'Kettlebells', icon: '⚫' },
    { id: 'yoga_mat', label: 'Yoga Mat', icon: '🧘' },
    { id: 'jump_rope', label: 'Jump Rope', icon: '🪢' }
  ];

  const toggleEquipment = (equipmentId) => {
    console.log('Toggle equipment:', equipmentId, 'Current:', formData.home_equipment);
    
    if (equipmentId === 'none') {
      // If selecting "none", clear all other equipment
      setFormData(prev => ({
        ...prev,
        home_equipment: prev.home_equipment.includes('none') ? [] : ['none']
      }));
    } else {
      // Remove "none" if selecting any equipment
      setFormData(prev => {
        const newEquipment = prev.home_equipment.filter(e => e !== 'none');
        if (newEquipment.includes(equipmentId)) {
          return { ...prev, home_equipment: newEquipment.filter(e => e !== equipmentId) };
        } else {
          return { ...prev, home_equipment: [...newEquipment, equipmentId] };
        }
      });
    }
  };

  const handleSubmit = async () => {
    if (!legalAcknowledged) {
      alert('Please acknowledge the Medical Disclaimer & Liability Waiver before continuing.');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Convert feet and inches to total inches
      const totalInches = formData.height_feet && formData.height_inches
        ? (parseInt(formData.height_feet) * 12) + parseInt(formData.height_inches)
        : null;
      
      const submitData = {
        skill_level: formData.skill_level,
        fitness_goal: formData.fitness_goal,
        home_equipment: formData.home_equipment,
        ...(formData.age && { age: parseInt(formData.age) }),
        ...(totalInches && { height: totalInches }),
        ...(formData.starting_weight && { starting_weight: parseFloat(formData.starting_weight) }),
        ...(formData.goal_weight && { goal_weight: parseFloat(formData.goal_weight) }),
        weight_unit: formData.weight_unit,
        ...(formData.weight_calibration && { weight_calibration: formData.weight_calibration }),
        ...(formData.injuries && { injuries: formData.injuries }),
        weak_point: formData.weak_point
      };

      await axios.post(`${API}/user/onboarding`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Record disclaimer acceptance
      try {
        await axios.post(`${API}/user/accept-disclaimer`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) { /* non-fatal */ }

      if (onComplete) {
        onComplete();
      } else {
        navigate('/main-hub');
      }
    } catch (error) {
      console.error('Onboarding error:', error);
      alert('Failed to complete onboarding. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black flex items-center justify-center p-4">
      {onLogout && (
        <button
          onClick={onLogout}
          className="absolute top-4 right-4 flex items-center gap-2 text-stone-500 hover:text-stone-300 text-sm transition-colors z-10"
          data-testid="onboarding-logout-btn"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      )}
      <Card className="glass p-8 max-w-3xl w-full border border-stone-800">
        {/* Progress Indicator */}
        <div className="flex justify-center mb-8">
          <div className="flex gap-2">
            {[1, 2, 3, 4].map(num => (
              <div
                key={num}
                className={`h-2 w-16 rounded-full transition-all ${
                  step >= num ? 'bg-blood-600' : 'bg-stone-700'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Step 1: Skill Level */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-blood-400 mb-2 tracking-wider">
                CHOOSE YOUR LEVEL
              </h2>
              <p className="text-stone-400">Select your current fitness experience</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => setFormData({ ...formData, skill_level: 'beginner' })}
                className={`glass p-6 rounded-xl transition-all border-2 ${
                  formData.skill_level === 'beginner'
                    ? 'border-blood-600 bg-blood-950/30'
                    : 'border-stone-700 hover:border-blood-800'
                }`}
              >
                <Dumbbell className="w-12 h-12 text-blood-500 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-blood-400 mb-2">BEGINNER</h3>
                <p className="text-stone-400 text-sm">New to fitness or returning after a break</p>
                {formData.skill_level === 'beginner' && (
                  <div className="mt-3 text-blood-400 font-bold text-sm flex items-center justify-center gap-2">
                    <Check className="w-4 h-4" />
                    SELECTED
                  </div>
                )}
              </button>

              <button
                onClick={() => setFormData({ ...formData, skill_level: 'intermediate' })}
                className={`glass p-6 rounded-xl transition-all border-2 ${
                  formData.skill_level === 'intermediate'
                    ? 'border-blood-600 bg-blood-950/30'
                    : 'border-stone-700 hover:border-blood-800'
                }`}
              >
                <Zap className="w-12 h-12 text-blood-500 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-blood-400 mb-2">INTERMEDIATE</h3>
                <p className="text-stone-400 text-sm">Regular training for 6+ months</p>
                {formData.skill_level === 'intermediate' && (
                  <div className="mt-3 text-blood-400 font-bold text-sm flex items-center justify-center gap-2">
                    <Check className="w-4 h-4" />
                    SELECTED
                  </div>
                )}
              </button>

              <button
                onClick={() => setFormData({ ...formData, skill_level: 'advanced' })}
                className={`glass p-6 rounded-xl transition-all border-2 ${
                  formData.skill_level === 'advanced'
                    ? 'border-blood-600 bg-blood-950/30'
                    : 'border-stone-700 hover:border-blood-800'
                }`}
              >
                <Trophy className="w-12 h-12 text-blood-500 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-blood-400 mb-2">ADVANCED</h3>
                <p className="text-stone-400 text-sm">Experienced athlete seeking challenges</p>
                {formData.skill_level === 'advanced' && (
                  <div className="mt-3 text-blood-400 font-bold text-sm flex items-center justify-center gap-2">
                    <Check className="w-4 h-4" />
                    SELECTED
                  </div>
                )}
              </button>
            </div>

            <Button
              onClick={() => setStep(2)}
              disabled={!formData.skill_level}
              className="w-full bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold py-6 rounded-xl tracking-wider"
            >
              CONTINUE
            </Button>
          </div>
        )}

        {/* Step 2: Fitness Goal */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-blood-400 mb-2 tracking-wider">
                FITNESS GOAL
              </h2>
              <p className="text-stone-400">What are you training for?</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => setFormData({ ...formData, fitness_goal: 'bulk' })}
                className={`glass p-6 rounded-xl transition-all border-2 ${
                  formData.fitness_goal === 'bulk'
                    ? 'border-blood-600 bg-blood-950/30'
                    : 'border-stone-700 hover:border-blood-800'
                }`}
              >
                <h3 className="text-xl font-bold text-blood-400 mb-2">💪 BULK</h3>
                <p className="text-stone-400 text-sm mb-2">Build muscle mass</p>
                <p className="text-xs text-stone-500">Heavy weights • 4-8 reps • Long rest</p>
                {formData.fitness_goal === 'bulk' && (
                  <div className="mt-3 text-blood-400 font-bold text-sm flex items-center justify-center gap-2">
                    <Check className="w-4 h-4" />
                    SELECTED
                  </div>
                )}
              </button>

              <button
                onClick={() => setFormData({ ...formData, fitness_goal: 'tone' })}
                className={`glass p-6 rounded-xl transition-all border-2 ${
                  formData.fitness_goal === 'tone'
                    ? 'border-blood-600 bg-blood-950/30'
                    : 'border-stone-700 hover:border-blood-800'
                }`}
              >
                <h3 className="text-xl font-bold text-blood-400 mb-2">✨ TONE</h3>
                <p className="text-stone-400 text-sm mb-2">Muscle definition</p>
                <p className="text-xs text-stone-500">Moderate weights • 12-15 reps • Short rest</p>
                {formData.fitness_goal === 'tone' && (
                  <div className="mt-3 text-blood-400 font-bold text-sm flex items-center justify-center gap-2">
                    <Check className="w-4 h-4" />
                    SELECTED
                  </div>
                )}
              </button>

              <button
                onClick={() => setFormData({ ...formData, fitness_goal: 'lose_weight' })}
                className={`glass p-6 rounded-xl transition-all border-2 ${
                  formData.fitness_goal === 'lose_weight'
                    ? 'border-blood-600 bg-blood-950/30'
                    : 'border-stone-700 hover:border-blood-800'
                }`}
              >
                <h3 className="text-xl font-bold text-blood-400 mb-2">🔥 LOSE WEIGHT</h3>
                <p className="text-stone-400 text-sm mb-2">Fat loss & cardio</p>
                <p className="text-xs text-stone-500">Light weights • 15-20 reps • Minimal rest</p>
                {formData.fitness_goal === 'lose_weight' && (
                  <div className="mt-3 text-blood-400 font-bold text-sm flex items-center justify-center gap-2">
                    <Check className="w-4 h-4" />
                    SELECTED
                  </div>
                )}
              </button>

              <button
                onClick={() => setFormData({ ...formData, fitness_goal: 'maintain' })}
                className={`glass p-6 rounded-xl transition-all border-2 ${
                  formData.fitness_goal === 'maintain'
                    ? 'border-blood-600 bg-blood-950/30'
                    : 'border-stone-700 hover:border-blood-800'
                }`}
              >
                <h3 className="text-xl font-bold text-blood-400 mb-2">⚖️ MAINTAIN</h3>
                <p className="text-stone-400 text-sm mb-2">General fitness</p>
                <p className="text-xs text-stone-500">Balanced • 8-12 reps • Standard rest</p>
                {formData.fitness_goal === 'maintain' && (
                  <div className="mt-3 text-blood-400 font-bold text-sm flex items-center justify-center gap-2">
                    <Check className="w-4 h-4" />
                    SELECTED
                  </div>
                )}
              </button>
            </div>

            <div className="mt-2">
              <h3 className="text-lg font-bold text-blood-400 tracking-wider text-center mb-1">
                WEAK POINT EMPHASIS
              </h3>
              <p className="text-stone-400 text-sm text-center mb-4">
                Any muscle group you want extra volume for? We&apos;ll prioritize it in your plans.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  { value: 'none', label: 'NONE' },
                  { value: 'chest', label: 'CHEST' },
                  { value: 'back', label: 'BACK' },
                  { value: 'shoulders', label: 'SHOULDERS' },
                  { value: 'arms', label: 'ARMS' },
                  { value: 'legs', label: 'LEGS' },
                  { value: 'glutes', label: 'GLUTES' },
                  { value: 'core', label: 'CORE' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setFormData({ ...formData, weak_point: opt.value })}
                    className={`glass p-3 rounded-lg border-2 font-semibold text-sm transition-all ${
                      formData.weak_point === opt.value
                        ? 'border-blood-600 bg-blood-950/30 text-blood-400'
                        : 'border-stone-700 text-stone-400 hover:border-blood-800'
                    }`}
                    data-testid={`onboarding-weak-point-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1 border-stone-700 text-stone-400 hover:bg-stone-800 py-6"
              >
                BACK
              </Button>
              <Button
                onClick={() => setStep(3)}
                className="flex-1 bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold py-6 rounded-xl tracking-wider"
              >
                CONTINUE
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Equipment */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-blood-400 mb-2 tracking-wider">
                HOME ARSENAL
              </h2>
              <p className="text-stone-400">Select equipment you have available at home</p>
              <p className="text-blood-500 font-bold mt-2">
                {formData.home_equipment.length} ITEM{formData.home_equipment.length !== 1 ? 'S' : ''} SELECTED
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {equipmentOptions.map(equipment => (
                <button
                  key={equipment.id}
                  onClick={() => toggleEquipment(equipment.id)}
                  className={`glass p-4 rounded-lg transition-all border-2 text-left ${
                    formData.home_equipment.includes(equipment.id)
                      ? 'border-blood-600 bg-blood-950/30 ring-2 ring-blood-500'
                      : 'border-stone-700 hover:border-blood-800'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{equipment.icon}</span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-blood-400">{equipment.label}</p>
                    </div>
                    {formData.home_equipment.includes(equipment.id) && (
                      <Check className="w-6 h-6 text-blood-500 flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setStep(2)}
                variant="outline"
                className="glass border-stone-700 text-stone-400 hover:bg-stone-800 font-semibold"
              >
                BACK
              </Button>
              <Button
                onClick={() => setStep(4)}
                className="flex-1 bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold py-6 rounded-xl tracking-wider"
              >
                CONTINUE
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Optional Profile Info */}
        {step === 4 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-blood-400 mb-2 tracking-wider">
                YOUR STATS (OPTIONAL)
              </h2>
              <p className="text-stone-400">Help us personalize your experience</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-blood-400 uppercase tracking-wider">Age</Label>
                <Input
                  type="number"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  className="bg-stone-900 border-stone-700 text-white mt-2"
                  placeholder="25"
                  data-testid="onboarding-age"
                />
              </div>

              <div>
                <Label className="text-blood-400 uppercase tracking-wider">Weight Calibration</Label>
                <p className="text-stone-500 text-xs mt-1 mb-2">
                  How should we calibrate your suggested weights?
                </p>
                <div className="flex gap-2">
                  {[
                    { value: 'lighter', label: 'LIGHTER RANGE' },
                    { value: 'standard', label: 'STANDARD RANGE' },
                    { value: 'heavier', label: 'HEAVIER RANGE' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setFormData({ ...formData, weight_calibration: opt.value })}
                      className={`flex-1 glass p-3 rounded-lg border-2 font-semibold text-sm ${
                        formData.weight_calibration === opt.value
                          ? 'border-blood-600 bg-blood-950/30 text-blood-400'
                          : 'border-stone-700 text-stone-400'
                      }`}
                      data-testid={`onboarding-weight-calibration-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-blood-400 uppercase tracking-wider">
                  Height
                </Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div>
                    <label className="text-stone-500 text-xs mb-1 block">Feet</label>
                    <Input
                      type="number"
                      min="0"
                      max="8"
                      value={formData.height_feet}
                      onChange={(e) => setFormData({ ...formData, height_feet: e.target.value })}
                      className="bg-stone-900 border-stone-700 text-white"
                      placeholder="5"
                    />
                  </div>
                  <div>
                    <label className="text-stone-500 text-xs mb-1 block">Inches</label>
                    <Input
                      type="number"
                      min="0"
                      max="11"
                      value={formData.height_inches}
                      onChange={(e) => setFormData({ ...formData, height_inches: e.target.value })}
                      className="bg-stone-900 border-stone-700 text-white"
                      placeholder="8"
                    />
                  </div>
                </div>
                <p className="text-stone-500 text-xs mt-1">
                  Example: 5&apos; 8&quot; (5 feet 8 inches)
                </p>
              </div>

              <div>
                <Label className="text-blood-400 uppercase tracking-wider">
                  Starting Weight ({formData.weight_unit})
                </Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.starting_weight}
                  onChange={(e) => setFormData({ ...formData, starting_weight: e.target.value })}
                  className="bg-stone-900 border-stone-700 text-white mt-2"
                  placeholder="180"
                />
              </div>

              <div>
                <Label className="text-blood-400 uppercase tracking-wider">
                  Goal Weight ({formData.weight_unit})
                </Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.goal_weight}
                  onChange={(e) => setFormData({ ...formData, goal_weight: e.target.value })}
                  className="bg-stone-900 border-stone-700 text-white mt-2"
                  placeholder="160"
                />
              </div>

              <div className="md:col-span-2">
                <Label className="text-blood-400 uppercase tracking-wider">Weight Unit</Label>
                <div className="flex gap-3 mt-2">
                  <button
                    onClick={() => setFormData({ ...formData, weight_unit: 'lbs' })}
                    className={`flex-1 glass p-3 rounded-lg border-2 font-semibold ${
                      formData.weight_unit === 'lbs'
                        ? 'border-blood-600 bg-blood-950/30 text-blood-400'
                        : 'border-stone-700 text-stone-400'
                    }`}
                  >
                    POUNDS (LBS)
                  </button>
                  <button
                    onClick={() => setFormData({ ...formData, weight_unit: 'kg' })}
                    className={`flex-1 glass p-3 rounded-lg border-2 font-semibold ${
                      formData.weight_unit === 'kg'
                        ? 'border-blood-600 bg-blood-950/30 text-blood-400'
                        : 'border-stone-700 text-stone-400'
                    }`}
                  >
                    KILOGRAMS (KG)
                  </button>
                </div>
              </div>

              <div className="md:col-span-2">
                <Label className="text-blood-400 uppercase tracking-wider">
                  Injuries or Limitations
                </Label>
                <p className="text-stone-500 text-xs mt-1 mb-2">
                  Any conditions the AI should consider when building your plan
                </p>
                <Input
                  type="text"
                  value={formData.injuries}
                  onChange={(e) => setFormData({ ...formData, injuries: e.target.value })}
                  className="bg-stone-900 border-stone-700 text-white"
                  placeholder="e.g., bad knees, shoulder injury, lower back pain"
                  data-testid="onboarding-injuries"
                />
              </div>
            </div>

            {/* Legal acknowledgement — required */}
            <div className="border-2 border-blood-800 rounded-lg p-4 bg-blood-950/10">
              <div className="flex items-start gap-3 mb-3">
                <Shield className="w-6 h-6 text-blood-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-base font-bold text-blood-400 tracking-wide mb-1">
                    MEDICAL DISCLAIMER &amp; LIABILITY WAIVER
                  </h3>
                  <p className="text-stone-300 text-xs leading-relaxed">
                    Anvil &amp; Ash is operated by individuals who are <strong className="text-white">not
                    physicians, licensed medical professionals, registered dietitians, physical
                    therapists, or certified personal trainers</strong>. Nothing in this app is medical
                    advice, diagnosis, treatment, therapy, or rehabilitation. Workouts, calorie
                    estimates, nutrition data, and AI suggestions are approximations for personal
                    informational use only — no guarantees of accuracy or outcome.
                    <br /><br />
                    <strong className="text-white">Consult a qualified physician before starting</strong>
                    {' '}any exercise or nutrition program. Stop immediately if you experience chest
                    pain, dizziness, lightheadedness, shortness of breath, sharp or persistent pain,
                    or any warning sign — and seek medical attention. You use this app entirely at
                    your own risk and release Anvil &amp; Ash and its operators from all liability.
                    Full text: <a
                      href="/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blood-400 underline hover:text-blood-300"
                    >Terms of Service</a>.
                  </p>
                </div>
              </div>
              <label
                htmlFor="onboarding-legal-ack"
                className="flex items-start gap-3 cursor-pointer select-none"
              >
                <Checkbox
                  id="onboarding-legal-ack"
                  checked={legalAcknowledged}
                  onCheckedChange={(v) => setLegalAcknowledged(!!v)}
                  className="mt-0.5 border-stone-500 data-[state=checked]:bg-blood-700 data-[state=checked]:border-blood-600"
                  data-testid="onboarding-legal-ack-checkbox"
                />
                <span className="text-stone-200 text-sm">
                  I have read and agree to the <strong className="text-white">Medical Disclaimer
                  &amp; Liability Waiver</strong>. I will consult a physician before starting and
                  I use this app entirely at my own risk.
                </span>
              </label>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setStep(3)}
                variant="outline"
                className="glass border-stone-700 text-stone-400 hover:bg-stone-800 font-semibold"
              >
                BACK
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || !formData.skill_level || !legalAcknowledged}
                className="flex-1 bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold py-6 rounded-xl tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="onboarding-complete-btn"
              >
                {loading ? 'FORGING...' : 'COMPLETE SETUP'}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Onboarding;
